/**
 * Response shapes from `/api/portal/*`.
 *
 * Separate from `api/types.ts` on purpose. The console's types describe the whole
 * system — every client, every delivery attempt, the redacted configs — and the
 * portal's describe one subscriber's own data. Sharing one file would put
 * `assigned_client` and `ChannelRow` a single import away from a screen a customer
 * is looking at, and the first accidental reuse of a console type here is the one
 * that renders someone else's field.
 *
 * Every type here is what the API actually returns rather than what the tables
 * hold: no client id on a lead (the session is the scope), no channel config, no
 * stored provider response. See api/routes/portal.ts.
 */

/**
 * Used until `/api/portal/summary` answers, which carries the server's own list.
 *
 * A hardcoded fallback exists so the outcome control is usable on a cold load
 * rather than an empty select; the server's list always wins once it arrives, and
 * an outcome this build has never heard of is still rendered (see
 * `PortalOutcomeDialog`).
 */
export const FALLBACK_OUTCOMES = ["delivered", "contacted", "quoted", "won", "lost", "bad_lead"];

/**
 * The signed-in portal user.
 *
 * The client is nested rather than sitting beside the user as a bare `clientId`,
 * because a flat id reads like something a request could change — and it is not.
 * The scope of every read comes from the session on the server.
 */
export interface PortalUser {
  id: string;
  email: string;
  name: string | null;
  /** `member` | `admin`, within this client. No console privilege of any kind. */
  role: string;
  lastLoginAt: string | null;
  createdAt: string;
  client: { id: string; name: string; status: string };
}

/**
 * The public-record evidence behind a lead, as the assembler stored it.
 *
 * Deliberately `unknown` per field rather than `string`: this is whatever the
 * upstream dataset had, and a permit's `balanceDue` is a number in one city and a
 * string in the next. The detail screen renders each through `String()`, which
 * cannot be wrong about a type it never claimed.
 */
export interface PortalEvidence {
  source?: unknown;
  reference?: unknown;
  status?: unknown;
  severity?: unknown;
  description?: unknown;
  deadline?: unknown;
  balanceDue?: unknown;
  sourceUrl?: unknown;
}

/** The parts of a lead's payload the portal reads by name. */
export interface PortalPayload {
  whyNow?: string | null;
  event?: PortalEvidence;
  organization?: { portfolioBuildings?: number | null };
}

/**
 * One row of the client's feed.
 *
 * `outcome*` is this client's own reported outcome and nobody else's — the API
 * joins `lead_outcomes` on the session's client id, so another subscriber's
 * feedback on the same lead is not reachable from here.
 */
export interface PortalLead {
  id: string;
  signalKey: string;
  signalLabel: string | null;
  signalKind: string | null;
  /** The reason sentence: why this property is worth a call today. */
  whyNow: string | null;
  quality: string;
  eventAt: string | null;
  createdAt: string;
  /** Null when the source gave no event date, not zero. */
  daysOld: number | null;
  /** When it was routed to this client, which is when they could first act on it. */
  assignedAt: string;
  market: string | null;
  industry: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  borough: string | null;
  resUnits: number | null;
  yearBuilt: string | null;
  propertyKind: string | null;
  /** The owner or managing company to call. */
  org: string | null;
  orgKind: string | null;
  outcome: string | null;
  outcomeNote: string | null;
  outcomeValueUsd: number | null;
  outcomeReportedAt: string | null;
}

export interface PortalLeadPage {
  items: PortalLead[];
  total: number;
  limit: number;
  offset: number;
}

/** A way to reach the owner. No provider name: which vendor found it is not the client's business. */
export interface PortalContact {
  kind: string;
  value: string;
  title: string | null;
  /** The provider's deliverability verdict, when it gave one. */
  verification: string | null;
  confidence: number | null;
}

/**
 * One delivery attempt over one of this client's own channels.
 *
 * Type and status only. A channel's config is the destination credential and the
 * stored `response` is the third party's raw reply — the API sends neither, and
 * this type is the reminder of why.
 */
export interface PortalDelivery {
  channelType: string | null;
  attempt: number;
  status: string;
  at: string;
}

export interface PortalLeadDetail {
  lead: PortalLead & {
    payload: PortalPayload | null;
    firedAt: string | null;
    signalDescription: string | null;
  };
  contacts: PortalContact[];
  deliveries: PortalDelivery[];
}

/** The standalone delivery log for one lead, for "did it actually reach us". */
export interface PortalDeliveryLog {
  leadId: string;
  items: PortalDelivery[];
}

export interface PortalSummary {
  client: { id: string; name: string; status: string };
  totals: {
    total: number;
    last7Days: number;
    /** Leads with no outcome reported. The one number worth acting on. */
    awaitingFeedback: number;
    won: number;
    valueUsd: number;
  };
  /** Counts per outcome, with unreported leads counted as `delivered`. */
  byOutcome: Array<{ outcome: string; count: number }>;
  /** The vocabulary the API accepts, so a form cannot offer a value it would reject. */
  outcomes: string[];
}
